import smCrypto from 'sm-crypto'
import EventBus from 'common-mediax/src/common/EventBus'
import { EventBusDataKey } from 'common-mediax/src/common/GlobalEvent'
import {
  MediaCloudSystemAuthoryOverview,
  MediacloudReporter,
} from 'common-mediax/src/apis'
/**
 * 用于加密数据
 * @param {any} msgString - 需加密的数据
 * @param {string} publicKey - 加密公钥
 * @return {string} 加密后的字符串
 */
export function doEncrypt(msgString, publicKey) {
  let msg = msgString
  if (typeof msgString !== 'string') {
    msg = JSON.stringify(msgString)
  }
  let sm2 = smCrypto.sm2
  // 1 - C1C3C2； 0 - C1C2C3； 默认为1
  let cipherMode = 0 // 特别注意,此处前后端需保持一致
  // 加密结果
  let encryptData = sm2.doEncrypt(msg, publicKey, cipherMode)
  // 加密后的密文前需要添加04，后端才能正常解密
  return '04' + encryptData
}
/**
 * Base64 转十六进制
 * @param {string} base64 - Base64 字符串
 * @return {string} 十六进制字符串
 */
function base64ToHex(base64) {
  try {
    // Base64 转二进制
    const binaryString = atob(base64)
    // 二进制转十六进制
    let hex = ''
    for (let i = 0; i < binaryString.length; i++) {
      const hexChar = binaryString.charCodeAt(i).toString(16)
      hex += (hexChar.length === 1 ? '0' : '') + hexChar
    }
    return hex
  } catch (e) {
    console.error('Base64 to Hex conversion failed:', e)
    return base64 // 转换失败返回原值
  }
}
/**
 * 用于解密数据
 * @param {any} enStr - 待解密的数据
 * @param {string | Object} privateKey - 解密私钥 当密钥不是字符串类型，说明是两重秘钥，需要使用sm4先解密，为object类型时，需要的格式为{SM4_PRI_KEY: string, SM2_PRI_KEY:string}
 * @return {string} 解密后的字符串
 */
export function doDecryptStr(enStr, privateKey) {
  let msg = enStr
  if (typeof enStr !== 'string') {
    msg = JSON.stringify(enStr)
  }
  // 解密的私钥不是字符串类型，说明是两重秘钥，需要使用sm4先解密
  if(typeof privateKey !== 'string'){
    // 使用sm4解密
    let sm4 = smCrypto.sm4
    // 1 - C1C3C2； 0 - C1C2C3； 默认为1
    let cipherMode = 1
    // 解密结果
    msg = base64ToHex(msg)
    msg = sm4.decrypt(msg, privateKey.SM4_PRI_KEY, cipherMode)
    privateKey = privateKey.SM2_PRI_KEY
  }
  let sm2 = smCrypto.sm2
  // 1 - C1C3C2； 0 - C1C2C3； 默认为1
  let cipherMode = 0
  // 加密后的密文，需要前去掉04。因为doDecrypt中自行添加了04，后端加密代码也自行添加了04
  let en = msg.substr(2)
  // 解密结果
  return sm2.doDecrypt(en, privateKey, cipherMode)
}

/**
 * 生成公私钥
 */
export function generatePubAndPriKey() {
  return new Promise((resolve) => {
    const APP_PUB_KEY =
      localStorage.getItem(EventBusDataKey.PUGC.APP_PUB_KEY) || ''
    const APP_PRI_KEY =
      localStorage.getItem(EventBusDataKey.PUGC.APP_PRI_KEY) || ''
    // if (
    //   localStorage.getItem('APP_PUB_KEY') &&
    //   localStorage.getItem('APP_PRI_KEY')
    // ) {
    //   return
    // }
    if (APP_PUB_KEY && APP_PRI_KEY) resolve(true)
    let sm2 = smCrypto.sm2
    let keypair = sm2.generateKeyPairHex()
    let publicKey = keypair.publicKey // 公钥
    let privateKey = keypair.privateKey // 私钥
    // localStorage.setItem('APP_PUB_KEY', publicKey)
    // localStorage.setItem('APP_PRI_KEY', privateKey)
    // console.log('----publicKey---', publicKey)
    // console.log('----privateKey---', privateKey)
    localStorage.setItem(EventBusDataKey.PUGC.APP_PUB_KEY, publicKey)
    localStorage.setItem(EventBusDataKey.PUGC.APP_PRI_KEY, privateKey)
    resolve(true)
  })
}
/**
 * 创建本地公私钥，同时向服务端获取SM4解密密钥
 * @param {string} userCode - 用户编码
 * @param {boolean} customRequest - 是否是自定义请求
 */
export async function createLocalPriAndPubKey(userCode,customRequest) {
  try{
    // 生成本地公私钥
    let sm2 = smCrypto.sm2
    let keypair = sm2.generateKeyPairHex()
    let publicKey = keypair.publicKey // 公钥
    let privateKey = keypair.privateKey // 私钥
    if(customRequest){
      return {
        publicKey,
        privateKey,
      }
    }
    let res = null;
    if(window.processEnvMode === 'PUGC') {
      // PUGC投稿平台
      res = await MediacloudReporter.setLocalPubKeyByPUGC({
        data:{
          pubKey: publicKey,
          userId: userCode
        }
      })
    }
    else {
      // 发送给服务端公钥，获取SM4解密密钥
      res = await MediaCloudSystemAuthoryOverview.setLocalPubKeyByLogin({
        data:{
          pubKey: publicKey,
          user:userCode
        }
      })
    }
    // 设置SM4解密密钥
    if(res.data.status === 0){
      EventBus.setData(EventBusDataKey.SECRET.SM2_PUB_KEY, publicKey)
      EventBus.setData(EventBusDataKey.SECRET.SM2_PRI_KEY, privateKey)
      EventBus.setData(EventBusDataKey.SECRET.SM4_PRI_KEY, res.data.data)
    }
  }catch (e) {
    console.error(e)
    throw e
  }
}
/**
 * 获取解密私钥,包含sm2私钥和sm4秘钥
 * @param isPromise 是否是异步获取秘钥
 */
export async function getDecryptPrivateKey(isPromise) {
    // 如果存在sm4私钥
    let SM4_PRI_KEY = EventBus.getData(EventBusDataKey.SECRET.SM4_PRI_KEY)
    let SM2_PRI_KEY = EventBus.getData(EventBusDataKey.SECRET.SM2_PRI_KEY)
    let SM2_PUB_KEY = EventBus.getData(EventBusDataKey.SECRET.SM2_PUB_KEY)
    if (SM4_PRI_KEY && SM2_PRI_KEY && SM2_PUB_KEY) {
      return {
        SM4_PRI_KEY,
        SM2_PRI_KEY,
      }
    }else {
      let userInfo = localStorage.getItem('loginInfo')
      userInfo = JSON.parse(userInfo)
      if(window.processEnvMode === 'PUGC' && userInfo.id) {
        await createLocalPriAndPubKey(userInfo.id)
      }
      else {
        await createLocalPriAndPubKey(userInfo.userCode)
      }
      SM4_PRI_KEY = EventBus.getData(EventBusDataKey.SECRET.SM4_PRI_KEY)
      SM2_PRI_KEY = EventBus.getData(EventBusDataKey.SECRET.SM2_PRI_KEY)
      return {
        SM4_PRI_KEY,
        SM2_PRI_KEY,
      }
    }
}

export function manualDecode(input) {
  if (!input) return null
  let result = ''
  let i = 0
  while (i < input.length) {
    if (input[i] === '%' && i + 2 < input.length) {
      let hex = input.substring(i + 1, i + 3)
      if (/^[0-9a-fA-F]{2}$/.test(hex)) {
        // 将十六进制转换为字节
        let byte = parseInt(hex, 16)
        // 检查是否是 UTF-8 多字节字符的开始
        if (byte >= 0xc0 && byte <= 0xdf) {
          // 两字节字符
          if (i + 5 < input.length && input[i + 3] === '%') {
            let hex2 = input.substring(i + 4, i + 6)
            if (/^[0-9a-fA-F]{2}$/.test(hex2)) {
              let byte2 = parseInt(hex2, 16)
              // 组合成 UTF-8 字符
              let codePoint = ((byte & 0x1f) << 6) | (byte2 & 0x3f)
              result += String.fromCodePoint(codePoint)
              i += 6
              continue
            }
          }
        } else if (byte >= 0xe0 && byte <= 0xef) {
          // 三字节字符
          if (
            i + 8 < input.length &&
            input[i + 3] === '%' &&
            input[i + 6] === '%'
          ) {
            let hex2 = input.substring(i + 4, i + 6)
            let hex3 = input.substring(i + 7, i + 9)
            if (
              /^[0-9a-fA-F]{2}$/.test(hex2) &&
              /^[0-9a-fA-F]{2}$/.test(hex3)
            ) {
              let byte2 = parseInt(hex2, 16)
              let byte3 = parseInt(hex3, 16)
              // 组合成 UTF-8 字符
              let codePoint =
                ((byte & 0x0f) << 12) | ((byte2 & 0x3f) << 6) | (byte3 & 0x3f)
              result += String.fromCodePoint(codePoint)
              i += 9
              continue
            }
          }
        } else {
          // 单字节字符
          result += String.fromCharCode(byte)
          i += 3
          continue
        }
      }
    }
    result += input[i]
    i++
  }
  if (result) {
    // 显式匹配零宽空格（U+200B），并覆盖格式字符(\p{Cf})中的空白类
    result = result
      .trim()
      // 匹配：普通空格(\s)、全角空格(\u3000)、零宽空格(\u200B)、零宽非断空格(\uFEFF)
      .replace(/[\s\u3000\u200B\uFEFF]+/gu, '')
      // 兜底匹配：所有空白分隔符(\p{Z}) + 格式字符中的零宽空格(\p{Cf}包含U+200B)
      .replace(/[\p{Z}\p{Cf}]+/gu, '')
      .replace(/\+/g, '')
  }
  return result
}

export function objectToQueryString(obj) {
  const str = []
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'object') {
      str.push(key + '=' + JSON.stringify(value))
    } else {
      str.push(key + '=' + value)
    }
    // str.push(encodeURIComponent(key) + '=' + encodeURIComponent(value))
  }
  return str.join('&')
}

// 获取11位随机数
export function getRandom11DigitNumber() {
  // 增强版随机数生成
  const crypto = window.crypto || window.msCrypto
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789'
  let result = ''

  // 混合使用两种随机源
  if (crypto && crypto.randomUUID) {
    result = crypto.randomUUID()
    result = result.split('-').join('')
  } else {
    // 回退方案：增强Math.random的随机性
    for (let i = 0; i < 32; i++) {
      result +=
        chars[
          Math.floor(Math.random() * 0x100000000 * performance.now()) %
            chars.length
        ]
    }
  }

  let now = Date.now() // 当前时间的毫秒数
  // let highResTime = performance.now() + '' // 高精度的时间，自页面加载起的毫秒数

  const timestamp = `${now.toFixed(0).slice(-5)}`
  return result + timestamp
}
